<div class="grid gap-6">
  <section class="grid gap-4 border-t border-line pt-5 first:border-t-0 first:pt-0">
    <app-ui-section-header
      label="Equipes"
      help="Grupos reutilizados por atendimento, operações e autorização."
    >
      <div sectionActions class="flex flex-wrap items-center gap-2">
        <button
          appButton
          type="button"
          variant="ghost"
          (click)="load()"
          [disabled]="loading()"
          aria-label="Atualizar equipes"
        >
          <ng-icon name="heroArrowPath" size="16" aria-hidden="true" />
        </button>
        <button
          appButton
          type="button"
          variant="primary"
          (click)="openCreateTeam()"
          [disabled]="!canCreateTeams()"
          [attr.title]="!canCreateTeams() ? createTeamPermissionHint : null"
        >
          <ng-icon name="heroPlus" size="16" aria-hidden="true" />
          Criar equipe
        </button>
      </div>
    </app-ui-section-header>

    <div appUiFilterBar>
      <label appUiFilterField class="min-w-64 flex-1 sm:max-w-sm">
        <span appUiFilterLabel>Buscar</span>
        <input
          appInput
          type="search"
          [value]="searchTerm()"
          (input)="setSearchTerm($any($event.target).value)"
          placeholder="Nome, área ou slug"
        />
      </label>
    </div>
  </section>

  <section class="grid gap-4 border-t border-line pt-5 first:border-t-0 first:pt-0">
    <app-ui-section-header
      [label]="'Lista de equipes (' + visibleTeams().length + ')'"
      help="Equipes ativas da organização. Arquivadas não aparecem nesta lista."
    />

    <div appUiDataTableShell>
      <table appUiDataTable>
        <thead appUiDataTableHead>
          <tr>
            <th appUiDataTableHeaderCell class="w-[34%]">Equipe</th>
            <th appUiDataTableHeaderCell class="w-[24%]">Área</th>
            <th appUiDataTableHeaderCell class="w-[28%]">Slug</th>
            <th appUiDataTableHeaderCell class="w-[14%] text-right">Ações</th>
          </tr>
        </thead>
        <tbody>
          @for (team of visibleTeams(); track team.id) {
            <tr appUiDataTableRow>
              <td appUiDataTableCell>
                <span class="block truncate font-semibold text-ink">{{ team.name }}</span>
              </td>
              <td appUiDataTableCell class="text-muted">
                <span class="block truncate">{{ teamKindLabel(team.kind) }}</span>
              </td>
              <td appUiDataTableCell class="text-muted">
                <span class="block truncate">{{ team.slug }}</span>
              </td>
              <td appUiDataTableCell>
                <div appUiDataTableActions>
                  <button
                    appButton
                    type="button"
                    variant="secondary"
                    size="sm"
                    (click)="openEditTeam(team)"
                    [disabled]="isTeamSaving(team.id) || !canUpdateTeams()"
                    [attr.title]="!canUpdateTeams() ? updateTeamPermissionHint : null"
                    aria-label="Editar equipe"
                  >
                    <ng-icon name="heroPencil" size="15" aria-hidden="true" />
                  </button>
                  <button
                    appButton
                    type="button"
                    variant="ghost"
                    size="sm"
                    (click)="archiveTeam(team)"
                    [disabled]="isTeamSaving(team.id) || !canArchiveTeams()"
                    [attr.title]="!canArchiveTeams() ? archiveTeamPermissionHint : null"
                    aria-label="Excluir equipe"
                  >
                    <ng-icon name="heroTrash" size="15" aria-hidden="true" />
                  </button>
                </div>
              </td>
            </tr>
          } @empty {
            <tr>
              <td appUiDataTableEmptyCell colspan="4">
                <app-ui-empty-state
                  compact
                  title="Nenhuma equipe encontrada"
                  description="Equipes criadas para atendimento, vendas ou operações aparecerão aqui."
                />
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  </section>

  @if (creatingTeam()) {
    <div appDialogOverlay>
      <section
        appDialog
        class="flex max-h-[calc(100dvh-2rem)] !w-[min(34rem,calc(100vw-2rem))] !max-w-none flex-col overflow-hidden"
      >
        <div class="shrink-0 border-b border-line pb-4">
          <div class="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 appDialogTitle class="text-base">Criar equipe</h3>
              <p appDialogDescription>
                Defina o grupo e, se quiser, selecione os membros iniciais.
              </p>
            </div>
            <button
              appButton
              type="button"
              variant="ghost"
              size="sm"
              (click)="closeCreateTeam()"
              aria-label="Fechar"
            >
              <ng-icon name="heroXMark" size="16" aria-hidden="true" />
            </button>
          </div>
        </div>

        <form
          class="min-h-0 overflow-y-auto overflow-x-hidden pt-5"
          [formGroup]="createTeamForm"
          (ngSubmit)="createTeam()"
        >
          <div class="grid gap-4">
            <label class="grid gap-1.5">
              <span appUiLabel>Nome da equipe</span>
              <input
                appInput
                formControlName="name"
                autocomplete="off"
                placeholder="Atendimento comercial"
                [readonly]="!canCreateTeams()"
              />
            </label>

            <div class="grid gap-1.5">
              <span appUiLabel id="new-team-kind-label"
                >Área principal</span
              >
              <div
                appSelect
                aria-labelledby="new-team-kind-label"
                [ngpSelectValue]="createTeamForm.controls.kind.value"
                [ngpSelectDisabled]="!canCreateTeams()"
                (ngpSelectValueChange)="selectCreateKind($event)"
              >
                <span>{{ teamKindLabel(createTeamForm.controls.kind.value) }}</span>
                <div *ngpSelectPortal appSelectDropdown>
                  @for (kind of teamKindOptions; track kind.value) {
                    <div appSelectOption [ngpSelectOptionValue]="kind.value">{{ kind.label }}</div>
                  }
                </div>
              </div>
            </div>

            <div class="grid gap-1.5">
              <span appUiLabel id="new-team-members-label"
                >Membros iniciais</span
              >
              <div
                appSelect
                aria-labelledby="new-team-members-label"
                [ngpSelectMultiple]="true"
                [ngpSelectValue]="createTeamMemberIds()"
                [ngpSelectDisabled]="!canManageTeamMembers()"
                (ngpSelectValueChange)="selectCreateTeamMembers($event)"
                [attr.title]="!canManageTeamMembers() ? manageMembersPermissionHint : null"
              >
                <span>{{ membershipSelectionLabel(createTeamMemberIds()) }}</span>
                <div *ngpSelectPortal appSelectDropdown>
                  @for (membership of memberships(); track membership.id) {
                    <div appSelectOption [ngpSelectOptionValue]="membership.id">
                      {{ membershipLabel(membership.id) }}
                      @if (createTeamMemberIds().includes(membership.id)) {
                        <ng-icon name="heroCheck" size="15" aria-hidden="true" />
                      }
                    </div>
                  }
                </div>
              </div>
            </div>

            <div class="flex justify-end gap-2">
              <button appButton type="button" variant="secondary" (click)="closeCreateTeam()">
                Cancelar
              </button>
              <button
                appButton
                type="submit"
                variant="primary"
                [disabled]="
                  saving() ||
                  createTeamForm.invalid ||
                  !canCreateTeams() ||
                  (createTeamMemberIds().length > 0 && !canManageTeamMembers())
                "
              >
                <ng-icon name="heroPlus" size="16" aria-hidden="true" />
                Criar equipe
              </button>
            </div>
          </div>
        </form>
      </section>
    </div>
  }

  @if (editingTeam(); as team) {
    <div appDialogOverlay>
      <section
        appDialog
        class="flex h-[min(50rem,calc(100dvh-2rem))] !w-[min(60rem,calc(100vw-2rem))] !max-w-none flex-col overflow-hidden"
      >
        <div class="shrink-0 border-b border-line pb-4">
          <div class="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 appDialogTitle class="text-base">Editar {{ team.name }}</h3>
              <p appDialogDescription>
                Atualize membros, funções herdadas e configurações operacionais da equipe.
              </p>
            </div>
            <button
              appButton
              type="button"
              variant="ghost"
              size="sm"
              (click)="closeEditTeam()"
              aria-label="Fechar"
            >
              <ng-icon name="heroXMark" size="16" aria-hidden="true" />
            </button>
          </div>
        </div>

        <div class="min-h-0 flex-1 overflow-y-auto overflow-x-hidden py-5 pr-1">
          <form
            class="grid gap-3 lg:grid-cols-[minmax(0,1fr)_14rem_auto]"
            [formGroup]="editTeamForm"
            (ngSubmit)="updateTeam()"
          >
            <label class="grid gap-1.5">
              <span appUiLabel>Nome</span>
              <input
                appInput
                formControlName="name"
                [readonly]="isTeamSaving(team.id) || !canUpdateTeams()"
                [attr.title]="!canUpdateTeams() ? updateTeamPermissionHint : null"
              />
            </label>
            <div class="grid gap-1.5">
              <span appUiLabel id="edit-team-kind-label"
                >Área principal</span
              >
              <div
                appSelect
                aria-labelledby="edit-team-kind-label"
                [ngpSelectValue]="editTeamForm.controls.kind.value"
                [ngpSelectDisabled]="isTeamSaving(team.id) || !canUpdateTeams()"
                (ngpSelectValueChange)="selectEditKind($event)"
              >
                <span>{{ teamKindLabel(editTeamForm.controls.kind.value) }}</span>
                <div *ngpSelectPortal appSelectDropdown>
                  @for (kind of teamKindOptions; track kind.value) {
                    <div appSelectOption [ngpSelectOptionValue]="kind.value">{{ kind.label }}</div>
                  }
                </div>
              </div>
            </div>
            <div class="flex items-end">
              <button
                appButton
                type="submit"
                variant="primary"
                [disabled]="isTeamSaving(team.id) || editTeamForm.invalid || !canUpdateTeams()"
                [attr.title]="!canUpdateTeams() ? updateTeamPermissionHint : null"
              >
                Salvar
              </button>
            </div>
          </form>

          <div class="mt-5 grid gap-5 xl:grid-cols-2">
            <section appUiPanelCard class="grid gap-4">
              <app-ui-section-header
                label="Membros"
                help="Selecione quem pertence à equipe e ajuste o papel operacional de cada membro."
              />
              <div class="grid gap-1.5">
                <span appUiLabel id="team-members-label"
                  >Selecionar membros</span
                >
                <div
                  appSelect
                  aria-labelledby="team-members-label"
                  [ngpSelectMultiple]="true"
                  [ngpSelectValue]="selectedTeamMemberIds()"
                  [ngpSelectDisabled]="isTeamSaving(team.id) || !canManageTeamMembers()"
                  (ngpSelectValueChange)="selectSelectedTeamMembers($event)"
                  [attr.title]="!canManageTeamMembers() ? manageMembersPermissionHint : null"
                >
                  <span>{{ membershipSelectionLabel(selectedTeamMemberIds()) }}</span>
                  <div *ngpSelectPortal appSelectDropdown>
                    @for (membership of memberships(); track membership.id) {
                      <div appSelectOption [ngpSelectOptionValue]="membership.id">
                        {{ membershipLabel(membership.id) }}
                        @if (selectedTeamMemberIds().includes(membership.id)) {
                          <ng-icon name="heroCheck" size="15" aria-hidden="true" />
                        }
                      </div>
                    }
                  </div>
                </div>
              </div>

              <div appUiDataTableShell>
                <table appUiDataTable>
                  <thead appUiDataTableHead>
                    <tr>
                      <th appUiDataTableHeaderCell class="w-[44%]">Membro</th>
                      <th appUiDataTableHeaderCell class="w-[24%]">Papel</th>
                      <th appUiDataTableHeaderCell class="w-[32%] text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (member of teamMembers(); track member.membershipId) {
                      <tr appUiDataTableRow>
                        <td appUiDataTableCell>
                          <span class="block truncate font-semibold text-ink">{{
                            membershipLabel(member.membershipId)
                          }}</span>
                          @if (isTeamMemberSaving(member)) {
                            <span class="mt-1 block text-xs font-semibold text-accent-strong">
                              Salvando...
                            </span>
                          }
                        </td>
                        <td appUiDataTableCell class="text-muted">
                          <span class="block truncate">{{ memberRoleLabel(member.role) }}</span>
                        </td>
                        <td appUiDataTableCell>
                          <div appUiDataTableActions>
                            <button
                              appButton
                              type="button"
                              variant="secondary"
                              size="sm"
                              (click)="
                                updateTeamMemberRole(
                                  member,
                                  member.role === 'lead' ? 'member' : 'lead'
                                )
                              "
                              [disabled]="isTeamMemberSaving(member) || !canManageTeamMembers()"
                              [attr.title]="
                                !canManageTeamMembers() ? manageMembersPermissionHint : null
                              "
                            >
                              {{ member.role === 'lead' ? 'Membro' : 'Líder' }}
                            </button>
                            <button
                              appButton
                              type="button"
                              variant="ghost"
                              size="sm"
                              (click)="removeTeamMember(member)"
                              [disabled]="isTeamMemberSaving(member) || !canManageTeamMembers()"
                              [attr.title]="
                                !canManageTeamMembers() ? manageMembersPermissionHint : null
                              "
                              aria-label="Remover membro"
                            >
                              <ng-icon name="heroTrash" size="15" aria-hidden="true" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    } @empty {
                      <tr>
                        <td appUiDataTableEmptyCell colspan="3">
                          <app-ui-empty-state
                            compact
                            title="Nenhum membro nesta equipe"
                            description="Membros adicionados à equipe selecionada aparecerão aqui."
                          />
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            </section>

            <section appUiPanelCard class="grid gap-4">
              <app-ui-section-header
                label="Calendário e atendimento"
                help="Configurações operacionais da equipe para atendimento, calendário e funções herdadas."
              />
              <div appUiPanelCard class="bg-soft">
                <p appUiPanelText class="!mt-0">
                  Calendário de atendimento e regras operacionais da equipe serão conectados aos
                  calendários comerciais da organização.
                </p>
              </div>

              <div class="grid gap-1.5">
                <span appUiLabel id="team-roles-label"
                  >Funções herdadas pela equipe</span
                >
                <div
                  appSelect
                  aria-labelledby="team-roles-label"
                  [ngpSelectMultiple]="true"
                  [ngpSelectValue]="selectedTeamRoleIds()"
                  [ngpSelectDisabled]="isTeamSaving(team.id) || !canAssignRoles()"
                  (ngpSelectValueChange)="selectSelectedTeamRoles($event)"
                  [attr.title]="!canAssignRoles() ? assignRolesPermissionHint : null"
                >
                  <span>{{ roleSelectionLabel(selectedTeamRoleIds()) }}</span>
                  <div *ngpSelectPortal appSelectDropdown>
                    @for (role of assignableRoles(); track role.id) {
                      <div appSelectOption [ngpSelectOptionValue]="role.id">
                        {{ role.name }}
                        @if (selectedTeamRoleIds().includes(role.id)) {
                          <ng-icon name="heroCheck" size="15" aria-hidden="true" />
                        }
                      </div>
                    }
                  </div>
                </div>
              </div>

              <div appUiDataTableShell>
                <table appUiDataTable>
                  <thead appUiDataTableHead>
                    <tr>
                      <th appUiDataTableHeaderCell>Função herdada</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (roleId of selectedTeamRoleIds(); track roleId) {
                      <tr appUiDataTableRow>
                        <td appUiDataTableCell class="font-semibold text-ink">
                          {{ roleLabel(roleId) }}
                        </td>
                      </tr>
                    } @empty {
                      <tr>
                        <td appUiDataTableEmptyCell>
                          <app-ui-empty-state
                            compact
                            title="Nenhuma função herdada"
                            description="Funções herdadas pelos membros desta equipe aparecerão aqui."
                          />
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </div>
      </section>
    </div>
  }

  @if (deletingTeam(); as team) {
    <div appDialogOverlay>
      <section appDialog>
        <div class="flex items-start justify-between gap-3">
          <div>
            <p class="text-xs font-semibold uppercase tracking-wide text-accent">Excluir equipe</p>
            <h3 appDialogTitle class="mt-1 text-base">{{ team.name }}</h3>
            <p appDialogDescription>
              A equipe será removida das listas ativas. Membros não perdem acesso à organização.
            </p>
            <p class="mt-2 text-sm font-semibold text-ink">
              {{ deleteTeamMembers().length }} membros estão vinculados a esta equipe.
            </p>
          </div>
          <button
            appButton
            type="button"
            variant="ghost"
            size="sm"
            (click)="closeDeleteTeam()"
            aria-label="Fechar"
          >
            <ng-icon name="heroXMark" size="16" aria-hidden="true" />
          </button>
        </div>

        @if (canTransferDeletingTeamMembers()) {
          <div class="mt-5 grid gap-1.5">
            <span appUiLabel id="delete-team-transfer-label"
              >Transferir membros</span
            >
            <div
              appSelect
              aria-labelledby="delete-team-transfer-label"
              [ngpSelectValue]="deleteTeamTransferId()"
              [ngpSelectDisabled]="saving() || !canManageTeamMembers()"
              (ngpSelectValueChange)="selectDeleteTeamTransfer($event)"
              [attr.title]="!canManageTeamMembers() ? manageMembersPermissionHint : null"
            >
              <span>{{ deleteTeamTransferLabel() }}</span>
              <div *ngpSelectPortal appSelectDropdown>
                <div appSelectOption ngpSelectOptionValue="">Não transferir</div>
                @for (targetTeam of deleteTransferTeams(); track targetTeam.id) {
                  <div appSelectOption [ngpSelectOptionValue]="targetTeam.id">
                    {{ targetTeam.name }}
                  </div>
                }
              </div>
            </div>
            <p appUiHint>
              {{ deleteTeamMembers().length }} membros encontrados nesta equipe.
            </p>
          </div>
        }

        <div class="mt-5 flex justify-end gap-2">
          <button appButton type="button" variant="secondary" (click)="closeDeleteTeam()">
            Cancelar
          </button>
          <button
            appButton
            type="button"
            variant="danger"
            (click)="confirmDeleteTeam()"
            [disabled]="saving() || !canArchiveTeams()"
          >
            <ng-icon name="heroTrash" size="16" aria-hidden="true" />
            Excluir equipe
          </button>
        </div>
      </section>
    </div>
  }
</div>
